import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, verification, type VerificationDto } from '../net/api';
import { Field } from './Field';

/** Account panel: verification status, and the application for players 15 and over. */
export function VerificationSection({ email }: { email?: string | null }) {
  const [v, setV] = useState<VerificationDto>();
  const [open, setOpen] = useState(false);
  const [codeSent, setCodeSent] = useState<string>();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string; fields?: Record<string, string> }>();
  const load = () => verification.mine().then(setV, () => {});
  useEffect(() => {
    void load();
  }, []);
  if (!v) return null;

  async function sendCode() {
    try {
      const r = await verification.emailCode();
      setCodeSent(r.to);
      setMsg(undefined);
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  }
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const s = (k: string) => String(f.get(k) ?? '').trim();
    try {
      const r = await verification.apply({ email: s('email'), emailCode: s('emailCode'), phone: s('phone'), birthDate: s('birthDate'), twoStepCode: s('twoStepCode') });
      setMsg({ kind: 'ok', text: r.status === 'waiting_parent' ? 'Sent! Your parent needs to approve it first, then our team reviews it.' : 'Sent! Our team will review your request.' });
      setOpen(false);
      await load();
    } catch (err) {
      setMsg({ kind: 'err', text: (err as Error).message, fields: err instanceof ApiError ? err.fields : undefined });
    }
  }

  let body: React.ReactNode;
  if (v.verified) {
    body = <p className="note-ok">Your account is verified{v.shownPublicly ? ', and it shows on your profile.' : '. It shows on your profile once you’re 17.'}</p>;
  } else if (v.request?.status === 'waiting_parent') {
    body = <p className="muted small-print">Your request is waiting for your parent to approve it in their parent controls.</p>;
  } else if (v.request?.status === 'pending') {
    body = <p className="muted small-print">Your request is with our team for review.</p>;
  } else if (!v.canApply) {
    body = <p className="muted small-print">Verification is for players 15 and over.</p>;
  } else if (!open) {
    body = (
      <div className="stack">
        {v.request?.status === 'declined' && <p className="form-error small-print">Your last request wasn’t approved{v.request.note ? `: ${v.request.note}` : '.'}</p>}
        <p className="muted small-print">
          Verified players see fewer automatic account checks and can send private messages to players their own age. You’ll need your confirmed email, a phone number, your date of birth and two-step verification
          {v.needsParent ? ', and your parent approves the request first' : ''}.
        </p>
        <button className="btn small ghost" onClick={() => setOpen(true)}>Get verified</button>
      </div>
    );
  } else if (!v.emailConfirmed || !v.twoStep) {
    body = (
      <p className="form-error small-print">
        First {[!v.emailConfirmed && 'confirm your email', !v.twoStep && 'turn on two-step verification (above)'].filter(Boolean).join(' and ')}.
      </p>
    );
  } else {
    body = (
      <form className="stack" onSubmit={submit} noValidate>
        <Field id="v-email" name="email" label="Your account’s email" type="email" defaultValue={email ?? ''} required error={msg?.fields?.email} />
        <div className="row-wrap">
          <button type="button" className="btn small ghost" onClick={() => void sendCode()}>{codeSent ? 'Send a new code' : 'Email me a code'}</button>
          {codeSent && <span className="muted small-print">Sent to {codeSent}. It works for 15 minutes.</span>}
        </div>
        <Field id="v-email-code" name="emailCode" label="6-digit code from the email" inputMode="numeric" required error={msg?.fields?.emailCode} />
        <Field id="v-phone" name="phone" label="Phone number (+1 area code)" type="tel" autoComplete="tel" required error={msg?.fields?.phone} />
        <Field id="v-birth" name="birthDate" label="Your date of birth" type="date" required error={msg?.fields?.birthDate} />
        <Field id="v-two-step" name="twoStepCode" label="Code from your authenticator app" autoComplete="one-time-code" required />
        <p className="muted small-print">Your phone number is only seen by our review team, and one number can verify one account.</p>
        <div className="row-wrap">
          <button className="btn small">Send request</button>
          <button type="button" className="btn small ghost" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      </form>
    );
  }
  return (
    <>
      <h3 className="mini-head">Verification</h3>
      {body}
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
    </>
  );
}

/** Family page: a 15 or 16-year-old's verification request waits for the parent's OK. */
export function ChildVerification({ childId, username }: { childId: number; username: string }) {
  const [v, setV] = useState<{ verified: boolean; request: { status: string } | null }>();
  const [msg, setMsg] = useState<string>();
  const load = () => verification.child(childId).then(setV, () => {});
  useEffect(() => {
    void load();
  }, [childId]);
  if (!v || (!v.verified && v.request?.status !== 'waiting_parent')) return null;
  const decide = async (d: 'approve' | 'decline' | 'withdraw') => {
    try {
      await verification.decideChild(childId, d);
      setMsg(d === 'approve' ? 'Approved. Our team will review it next.' : d === 'decline' ? 'Declined.' : 'Withdrawn. The account is no longer verified.');
      await load();
    } catch (e) {
      setMsg((e as Error).message);
    }
  };
  return (
    <div className="stack">
      <h3>Verification</h3>
      {v.verified ? (
        <>
          <p className="muted small-print">{username} is verified, which lets them send private messages to players their own age.</p>
          <button className="btn ghost" onClick={() => void decide('withdraw')}>Withdraw my approval</button>
        </>
      ) : (
        <>
          <p className="muted small-print">{username} asked to be verified. Verified players see fewer automatic account checks and can send private messages to players their own age (and to you). Our team reviews it after you approve.</p>
          <div className="row-wrap">
            <button className="btn" onClick={() => void decide('approve')}>Approve</button>
            <button className="btn ghost" onClick={() => void decide('decline')}>Decline</button>
          </div>
        </>
      )}
      {msg && <p className="note-ok" role="status">{msg}</p>}
    </div>
  );
}
